import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setPassword } from '../../api/password.api';
import type { SetPasswordInput } from '../../api/password.api';
import {
  firstFailingNewPasswordField,
  newPasswordErrors,
  newPasswordFailures,
  passwordRuleState,
  UNTOUCHED_NEW_PASSWORD,
} from '../../domain/newPasswordRules';
import type { NewPasswordErrors, NewPasswordField, NewPasswordValues, PasswordRuleState } from '../../domain/newPasswordRules';
import { formProblemOf } from '../../domain/refusal';
import type { FormProblem } from '../../domain/refusal';
import type { PasswordGrant } from '../../types/domain.types';
import { passcodeMutationKeys } from '../logic/mutationKeys';

const RULE_REFUSALS: readonly string[] = ['TOO_SHORT', 'PASSWORD_TOO_SHORT', 'MISMATCH'];

export interface NewPasswordStep {
  readonly values: NewPasswordValues;
  readonly errors: NewPasswordErrors;
  readonly rule: PasswordRuleState;
  readonly failure: FormProblem | null;
  readonly saving: boolean;
  readonly waitingForNetwork: boolean;
  readonly setValue: (field: NewPasswordField, value: string) => void;
  readonly markTouched: (field: NewPasswordField) => void;
  readonly save: () => NewPasswordField | null;
  readonly skip: () => void;
}

export interface NewPasswordOptions {
  readonly grant: PasswordGrant;
  readonly minLength: number;
  readonly onFinished: () => void;
}

const EMPTY: NewPasswordValues = { next: '', confirm: '' };

const sentUnchanged = (values: NewPasswordValues, sent: SetPasswordInput | undefined): boolean =>
  sent !== undefined && sent.newPassword === values.next && sent.confirmPassword === values.confirm;

export const useNewPassword = ({ grant, minLength, onFinished }: NewPasswordOptions): NewPasswordStep => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<NewPasswordValues>(EMPTY);
  const [touched, setTouched] = useState(UNTOUCHED_NEW_PASSWORD);
  const [submitted, setSubmitted] = useState(false);
  const { mutate, isPending, isPaused, error, variables } = useMutation({
    mutationKey: passcodeMutationKeys.setPassword(),
    mutationFn: (input: SetPasswordInput) => setPassword(input, undefined),
  });
  const saving = (): boolean => queryClient.isMutating({ mutationKey: passcodeMutationKeys.setPassword() }) > 0;

  const problem = error === null ? null : formProblemOf(error);
  const ruleRefusal = problem?.kind === 'refused' && problem.refusal.code !== null && RULE_REFUSALS.includes(problem.refusal.code);
  const refusedByRules = ruleRefusal && sentUnchanged(values, variables);
  const shown = newPasswordErrors(values, { touched, submitted }, minLength);
  const errors: NewPasswordErrors = refusedByRules ? { ...shown, confirm: 'mismatch' } : shown;

  return {
    values,
    errors,
    rule: passwordRuleState(errors, values, minLength),
    failure: ruleRefusal ? null : problem,
    saving: isPending,
    waitingForNetwork: isPaused,
    setValue: (field, value) => setValues((current) => ({ ...current, [field]: value })),
    markTouched: (field) => setTouched((current) => (current[field] ? current : { ...current, [field]: true })),
    save: () => {
      setSubmitted(true);
      const failing = firstFailingNewPasswordField(newPasswordFailures(values, minLength));
      if (failing !== null) return failing;
      if (saving()) return null;
      mutate({ newPassword: values.next, confirmPassword: values.confirm, passwordGrant: grant.token }, { onSuccess: onFinished });
      return null;
    },
    skip: () => {
      if (saving()) return;
      onFinished();
    },
  };
};
